// Clientes Portal - Landing pública para clientes
//
// Nota: esta ruta existe como "slug" /clientes para el portal cliente.
// Desde aquí se inicia el flujo de contratación usando un hiring code.

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fadeIn, slideUp, staggerContainer, staggerItem } from '@/utils/animations';
import { useAuth } from '@/providers/AuthProvider';
import { getErrorMessage } from '@/services/api';
import { ClientesDashboard } from '@/pages/ClientesDashboard';

function normalizeIdentifier(raw: string): string {
  // Email: mantener (trim)
  // Teléfono: quitar espacios/guiones/paréntesis para que el backend pueda normalizar mejor
  const v = raw.trim();
  if (v.includes('@')) return v;
  return v.replace(/[()\s-]/g, '');
}

type LoginMode = 'otp' | 'hiring-code';
type LoginStep = 'select-mode' | 'hiring-code-enter' | 'hiring-code-verify' | 'otp-request' | 'otp-verify';

export function ClientesPortal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('portal');
  const {
    requestOtp,
    loginOtp,
    validateHiringCode,
    loginWithHiringCode,
    isAuthenticated,
    isLoading,
    user
  } = useAuth();

  // Estado del flujo
  const [, setLoginMode] = useState<LoginMode | null>(null);
  const [step, setStep] = useState<LoginStep>('select-mode');

  // Formularios
  const [hiringCode, setHiringCode] = useState('');
  const [hiringCodeData, setHiringCodeData] = useState<{
    hiring_code: string;
    client_name?: string;
    masked_email?: string;
    masked_phone?: string;
  } | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');

  // UI
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const normalizedIdentifier = useMemo(() => normalizeIdentifier(identifier), [identifier]);

  // Handler: Seleccionar modo de login
  const handleSelectMode = (mode: LoginMode) => {
    setLoginMode(mode);
    setStep(mode === 'otp' ? 'otp-request' : 'hiring-code-enter');
    setError(null);
    setInfo(null);
  };

  // Handler: Volver al inicio
  const handleBackToStart = () => {
    setLoginMode(null);
    setStep('select-mode');
    setHiringCode('');
    setHiringCodeData(null);
    setIdentifier('');
    setCode('');
    setError(null);
    setInfo(null);
  };

  // Handler: Validar código de contratación (Paso 1)
  const handleValidateHiringCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

if (!hiringCode.trim()) {
      setError(t('login.enterCodeError'));
      return;
    }

    setLoading(true);
    try {
const result = await validateHiringCode(hiringCode.trim().toUpperCase());
if (!result.valid) {
        console.warn('⚠️ [ClientesPortal] Código no válido');
        setError(t('login.invalidCode'));
        return;
      }

      setHiringCodeData({
        hiring_code: result.hiring_code,
        client_name: result.client_name,
        masked_email: result.masked_email,
        masked_phone: result.masked_phone,
      });

      console.log('✅ [ClientesPortal] Código validado, cambiando a paso 2');
      setStep('hiring-code-verify');
      setInfo(t('login.codeValidated'));
    } catch (err: unknown) {
      console.error('❌ [ClientesPortal] Error en validateHiringCode:', err);
      setError(getErrorMessage(err) || t('login.couldNotValidateCode'));
    } finally {
      setLoading(false);
    }
  };

  // Handler: Confirmar identidad con código de contratación (Paso 2)
  const handleLoginWithHiringCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    console.log('🔐 [ClientesPortal] handleLoginWithHiringCode - Identifier:', identifier.substring(0, 5) + '***');

    if (!normalizedIdentifier) {
      setError(t('login.enterEmailError'));
      return;
    }

    if (!hiringCodeData) {
      setError(t('login.codeNotValidated'));
      return;
    }

    setLoading(true);
    try {
      console.log('📡 [ClientesPortal] Llamando loginWithHiringCode...');
      await loginWithHiringCode(hiringCodeData.hiring_code, normalizedIdentifier);
      console.log('✅ [ClientesPortal] Login exitoso');

      const returnUrl = searchParams.get('returnUrl');
      if (returnUrl) {
        console.log('🔀 [ClientesPortal] Redirigiendo a:', returnUrl);
        navigate(returnUrl, { replace: true });
      } else {
        console.log('✅ [ClientesPortal] Quedándose en /clientes - mostrará dashboard');
      }
      // Si no hay returnUrl, nos quedamos en /clientes y mostramos el dashboard
    } catch (err: unknown) {
      console.error('❌ [ClientesPortal] Error en loginWithHiringCode:', err);
      setError(getErrorMessage(err) || t('login.identityMismatch'));
    } finally {
      setLoading(false);
    }
  };

  // Handler: Solicitar OTP
  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!normalizedIdentifier) {
      setError(t('login.enterEmailError'));
      return;
    }

    setLoading(true);
    try {
      await requestOtp(normalizedIdentifier);
      setStep('otp-verify');
      setInfo(t('login.otpSent'));
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('login.couldNotSendCode'));
    } finally {
      setLoading(false);
    }
  };

  // Handler: Verificar OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!normalizedIdentifier) {
      setError(t('login.enterEmailError'));
      return;
    }
    if (!code.trim()) {
      setError(t('login.enterCodeError'));
      return;
    }

    setLoading(true);
    try {
      await loginOtp(normalizedIdentifier, code.trim());

      const returnUrl = searchParams.get('returnUrl');
      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      }
      // Si no hay returnUrl, nos quedamos en /clientes y mostramos el estado de sesión
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('login.invalidOrExpiredCode'));
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      title: t('portal.features.verifyData'),
      description: t('portal.features.verifyDataDesc'),
    },
    {
      title: t('portal.features.kyc'),
      description: t('portal.features.kycDesc'),
    },
    {
      title: t('portal.features.payment'),
      description: t('portal.features.paymentDesc'),
    },
    {
      title: t('portal.features.contract'),
      description: t('portal.features.contractDesc'),
    },
  ];

  // Si está autenticado, mostrar dashboard directamente (ClientesDashboard ya usa ClientLayout)
  if (isAuthenticated && user && !isLoading) {
    console.log('🎉 [ClientesPortal] Usuario autenticado - Renderizando dashboard:', user.full_name);
    return <ClientesDashboard />;
  }

  return (
    <Layout>
      <motion.div
        className="max-w-5xl mx-auto px-4 py-12"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        {/* Hero Section */}
        <motion.div className="text-center mb-16" variants={slideUp}>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('portal.title')}
            <span className="block text-primary mt-2">{t('portal.brand')}</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('portal.subtitle')}
          </p>
        </motion.div>

        {/* Selección de modo de login */}
        {step === 'select-mode' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Login con código de contratación */}
            <div
              className="rounded-lg border-2 border-green-500 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105 bg-white"
              onClick={() => {
                console.log('🖱️ [ClientesPortal] Click en tarjeta: hiring-code');
                handleSelectMode('hiring-code');
              }}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">🎫</span>
                  <h3 className="text-xl font-semibold">{t('login.hiringCodeTitle')}</h3>
                </div>
                <p className="text-sm text-gray-600">
                  {t('login.hiringCodeDesc')}
                </p>
                <div className="pt-2 text-center">
                  <span className="text-green-600 font-medium">
                    {t('common.clickToContinue')}
                  </span>
                </div>
              </div>
            </div>

            {/* Login con OTP (email/teléfono) */}
            <div
              className="rounded-lg border-2 border-primary shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105 bg-white"
              onClick={() => {
                console.log('🖱️ [ClientesPortal] Click en tarjeta: otp');
                handleSelectMode('otp');
              }}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">📧</span>
                  <h3 className="text-xl font-semibold">{t('login.otpTitle')}</h3>
                </div>
                <p className="text-sm text-gray-600">
                  {t('login.otpDesc')}
                </p>
                <div className="pt-2 text-center">
                  <span className="text-primary font-medium">
                    {t('common.clickToContinue')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Flujo con código de contratación - Paso 1: Ingresar código */}
        {step === 'hiring-code-enter' && (
          <div className="max-w-md mx-auto">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>{t('login.enterHiringCode')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  {t('login.hiringCodeHint')}
                </p>
                <form onSubmit={handleValidateHiringCode} className="space-y-3">
                  <div>
                    <Label htmlFor="hiring-code">{t('login.hiringCodeLabel')}</Label>
                    <Input
                      id="hiring-code"
                      type="text"
                      placeholder="AB12C"
                      maxLength={5}
                      value={hiringCode}
                      onChange={(e) => {
                        setHiringCode(e.target.value.toUpperCase());
                        setError(null);
                        setInfo(null);
                      }}
                      disabled={isLoading || loading}
                      className="mt-1 uppercase text-center font-mono text-lg"
                      autoFocus
                    />
                  </div>

                  {info && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                      {info}
                    </div>
                  )}

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Button
                      type="submit"
                      disabled={isLoading || loading || hiringCode.length !== 5}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {loading ? t('login.validating') : t('common.continue')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBackToStart}
                      className="w-full"
                    >
                      {t('common.back')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Flujo con código de contratación - Paso 2: Confirmar identidad */}
        {step === 'hiring-code-verify' && hiringCodeData && (
          <div className="max-w-md mx-auto">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>{t('login.confirmIdentity')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                  <p className="text-sm font-medium text-green-900 mb-2">
                    {t('login.validCode', { code: hiringCodeData.hiring_code })}
                  </p>
                  {hiringCodeData.client_name && (
                    <p className="text-sm text-green-800">
                      {t('login.client', { name: hiringCodeData.client_name })}
                    </p>
                  )}
                </div>

                <p className="text-sm text-gray-600">
                  {t('login.confirmIdentityHint')}
                </p>

                {hiringCodeData.masked_email && (
                  <p className="text-xs text-gray-500">
                    {t('login.registeredEmail', { email: hiringCodeData.masked_email })}
                  </p>
                )}
                {hiringCodeData.masked_phone && (
                  <p className="text-xs text-gray-500">
                    {t('login.registeredPhone', { phone: hiringCodeData.masked_phone })}
                  </p>
                )}

                <form onSubmit={handleLoginWithHiringCode} className="space-y-3">
                  <div>
                    <Label htmlFor="identifier-code">{t('login.emailOrPhone')}</Label>
                    <Input
                      id="identifier-code"
                      type="text"
                      value={identifier}
                      onChange={(e) => {
                        setIdentifier(e.target.value);
                        setError(null);
                        setInfo(null);
                      }}
                      placeholder={t('login.emailOrPhonePlaceholder')}
                      disabled={isLoading || loading}
                      className="mt-1"
                    />
                  </div>

                  {info && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                      {info}
                    </div>
                  )}

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Button
                      type="submit"
                      disabled={isLoading || loading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {loading ? t('login.verifying') : t('login.access')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setStep('hiring-code-enter');
                        setHiringCodeData(null);
                        setIdentifier('');
                        setError(null);
                        setInfo(null);
                      }}
                      className="w-full"
                    >
                      {t('login.changeCode')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleBackToStart}
                      className="w-full text-xs"
                    >
                      {t('common.backToStart')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Flujo con OTP - Paso 1: Solicitar código */}
        {step === 'otp-request' && (
          <div className="max-w-md mx-auto">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>{t('login.loginTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleRequestOtp} className="space-y-3">
                  <div>
                    <Label htmlFor="identifier">{t('login.emailOrPhone')}</Label>
                    <Input
                      id="identifier"
                      type="text"
                      value={identifier}
                      onChange={(e) => {
                        setIdentifier(e.target.value);
                        setError(null);
                        setInfo(null);
                      }}
                      placeholder={t('login.emailOrPhonePlaceholder')}
                      disabled={isLoading || loading}
                      className="mt-1"
                    />
                  </div>

                  {info && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                      {info}
                    </div>
                  )}

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Button
                      type="submit"
                      disabled={isLoading || loading}
                      className="w-full bg-primary hover:bg-primary-700 text-white"
                    >
                      {loading ? t('login.sending') : t('login.sendCode')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBackToStart}
                      className="w-full"
                    >
                      {t('common.back')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Flujo con OTP - Paso 2: Verificar código */}
        {step === 'otp-verify' && (
          <div className="max-w-md mx-auto">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>{t('login.verifyCode')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleVerifyOtp} className="space-y-3">
                  <div>
                    <Label htmlFor="identifier-display">{t('login.emailOrPhone')}</Label>
                    <Input
                      id="identifier-display"
                      type="text"
                      value={identifier}
                      disabled
                      className="mt-1 bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {t('login.changeIdentifierHint')}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="otp-code">{t('login.otpCodeLabel')}</Label>
                    <Input
                      id="otp-code"
                      type="text"
                      value={code}
                      onChange={(e) => {
                        setCode(e.target.value);
                        setError(null);
                        setInfo(null);
                      }}
                      placeholder="123456"
                      disabled={isLoading || loading}
                      className="mt-1"
                      inputMode="numeric"
                    />
                  </div>

                  {info && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                      {info}
                    </div>
                  )}

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Button
                      type="submit"
                      disabled={isLoading || loading}
                      className="w-full bg-primary hover:bg-primary-700 text-white"
                    >
                      {loading ? t('login.verifying') : t('login.verifyAndEnter')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isLoading || loading}
                      className="w-full"
                      onClick={() => {
                        setStep('otp-request');
                        setCode('');
                        setInfo(null);
                        setError(null);
                      }}
                    >
                      {t('common.back')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isLoading || loading}
                      className="w-full"
                      onClick={async () => {
                        setError(null);
                        setInfo(null);
                        setLoading(true);
                        try {
                          await requestOtp(normalizedIdentifier);
                          setInfo(t('login.codeResent'));
                        } catch (err: unknown) {
                          setError(getErrorMessage(err) || t('login.couldNotResendCode'));
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      {t('login.resendCode')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Features */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          variants={staggerContainer}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={staggerItem}>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Info */}
        <motion.div className="mt-12 text-center text-sm text-gray-500" variants={fadeIn}>
          <p>{t('portal.noCodeInfo')}</p>
        </motion.div>
      </motion.div>
    </Layout>
  );
}
